/**
 * QL-660 — the words of "who answers on behalf of whom": the representative
 * roles (power of attorney / guardian / representative) and the connectors
 * around them. The phrases themselves are composed in `answering-attribution.ts`,
 * so the web, the PDF blocks and the AI tools never compose the relation
 * themselves. Sentence frames around these phrases stay in each surface's own
 * translation table.
 */

import { pickT } from "../i18n";
import type { ClientPartyRole } from "../types/client-party";
import type { TranslationTable } from "../types/translation-table";

type AnsweringWords = {
  /** Lower-case, for inline use ("… (power of attorney)"). */
  roles: Record<ClientPartyRole, string>;
  onBehalfOf: string;
  answeredBy: string;
  answersThrough: string;
};

const ANSWERING_WORDS: TranslationTable<AnsweringWords> = {
  en_GB: {
    roles: { poa: "power of attorney", guardian: "guardian", representative: "representative" },
    onBehalfOf: "on behalf of",
    answeredBy: "answered by",
    answersThrough: "answers through",
  },
  nb_NO: {
    roles: { poa: "fullmektig", guardian: "verge", representative: "representant" },
    onBehalfOf: "på vegne av",
    answeredBy: "besvart av",
    answersThrough: "svarer gjennom",
  },
};

/** The answering words in `language`; unknown locales fall back to en_GB. */
export function answeringWords(language: string): AnsweringWords {
  return pickT(ANSWERING_WORDS, language);
}

/** Lower-case role label for inline use ("… (power of attorney)"). Unknown locales fall back to en_GB. */
export function representativeRoleLabel(role: ClientPartyRole, language: string): string {
  return answeringWords(language).roles[role];
}
